/**
 * Normalizes post data from API responses
 * Handles different API response formats (feed, profile, saved posts)
 */
/**
 * Recursively normalize comments with replies
 */
export const normalizeComments = (commentsWrapper) => {
  const comments = commentsWrapper?.$values || [];

  const normalize = (comment) => ({
    id: comment.id,
    commentText: comment.commentText || '',
    createdAt: comment.createdAt,
    user: {
      id: comment?.user?.id,
      name: comment?.user?.name || 'Unknown User',
      profilePhotoUrl:
        comment?.user?.profilePhotoUrl || 'https://i.pravatar.cc/40',
    },
    replies: comment?.replies?.$values
      ? comment.replies.$values.map(normalize)
      : [],
  });
  return comments.map(normalize);
};

export const normalizePost = (post, sourceType = 'feed') => {
  if (!post?.id) return null;

  // Extract media with fallbacks
  const mediaValues = post?.media?.$values || [];
  const images = mediaValues.map(m => m?.mediaUrl).filter(Boolean);

  return {
    id: post.id,
    userId: post?.user?.id || post?.userId,
    userName: post?.user?.name || 'Unknown User',
    userPhoto: post?.user?.profilePhotoUrl ,
    
    content: post?.caption || '',
    
    image: images[0] || null,
    images: images,
    mediaType: mediaValues[0]?.mediaType || null,
    
    date: post?.createdAt,
    visibility: post?.visibility ?? 0,
    
    likes: post?.likesCount ?? 0,
    isLiked: post?.isLikedByMe ?? false,
    isSaved: post?.isSaved ?? false,
    
    commentsCount: post?.commentsCount ?? 0,
    //comments: post?.comments?.$values || [],
    comments: normalizeComments(post?.comments),
    
    shares: post?.sharesCount ?? 0,
  };
};


/**
 * Normalizes profile post data (different structure than feed posts)
 */
export const normalizeProfilePost = (post, user) => {
  if (!post?.id) return null;

  const mediaValues = post?.media?.$values || [];
  const images = mediaValues.filter(Boolean);

  return {
    id: post.id,
    userId: user?.id || user?.$id,
    userName: user?.name || 'Unknown User',
    userPhoto: user?.profilePhotoUrl,
    
    content: post?.caption || '',
    
    image: images[0] || null,
    images: images,
    
    date: post?.createdAt,
    
    likes: post?.likesCount || 0,
    commentsCount: post?.commentsCount || 0,
    
    comments: [],
    shares: 0,
    isLikedByMe: post?.isLikedByMe || false,
    isSaved: post?.isSaved || false,
  };
};

/**
 * Normalizes saved post data
 */
export const normalizeSavedPost = (post) => {
  if (!post?.id) return null;

  const mediaValues = post?.media?.$values || [];
  const images = mediaValues.filter(Boolean);

  return {
    id: post.id,
    userId: post?.user?.id || '',
    userName: post?.user?.name || 'Unknown User',
    userPhoto: post?.user?.profilePhotoUrl,
    
    content: post?.caption || '',
    
    image: images[0] || null,
    images: images,
    
    date: post?.createdAt,
    
    likes: post?.likesCount || 0,
    commentsCount: post?.commentsCount || 0,
    
    comments: [],
    shares: 0,
    isLiked: post?.isLikedByMe || false,
    isSaved: true, // Always true for saved posts
  };
};

/**
 * Formats a date for display
 */
export const formatPostDate = (dateString) => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Just now';
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    // Less than a minute
    if (diffInSeconds < 60) return 'Just now';
    
    // Less than an hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    }
    
    // Less than a day
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    }
    
    // Less than a week
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
    
    // Default: full date
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Just now';
  }
};

/**
 * Validates if a post object is valid
 */
export const isValidPost = (post) => {
  return Boolean(post?.id);
};

/**
 * Creates a temporary comment for optimistic updates
 */
// export const createTempComment = (commentText, userName = 'You') => ({
//   id: `temp-${Date.now()}`,
//   commentText,
//   user: { name: userName },
//   createdAt: new Date().toISOString(),
// });
/**
 * Creates a temporary comment for optimistic updates
 */
// postUtils.js
export function createTempComment(text, currentUser) {
  return {
    id: `temp-${Date.now()}`,
    commentText: text,
    createdAt: new Date().toISOString(),
    likesCount: 0,
    replies: [],
    user: {
      id: currentUser?.id,
      name: currentUser?.name || 'You',
      profilePhotoUrl: currentUser?.profilePhotoUrl || null,
    },
    isTemp: true,
  };
}

/**
 * Format count for display (e.g., 1234 -> 1.2K)
 */
export const formatCount = (count) => {
  if (count < 1000) return count.toString();
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
  return `${(count / 1000000).toFixed(1)}M`;
};